import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context
import '../../index.css'; // Import the CSS file directly

const Resilience = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'resilience'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container resilience-page">
      <h1 className="page-title">Building Emotional Resilience</h1>

      {/* Include the image */}
      <img
        src="/images/wf-resilience.webp"
        alt="Building Emotional Resilience"
        className="page-image"
      />

      <p className="page-text">
        Welcome to the Emotional Resilience section of Wholeness Forward. Emotional resilience is the ability to adapt, recover, and thrive in the face of stress, adversity, or challenges. 
        Building this skill can enhance your mental well-being, improve relationships, and help you navigate life's ups and downs with greater confidence and grace.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Health Benefits of Emotional Resilience</h2>
      <ul className="content-list">
        <li><strong>Reduced Stress:</strong> Helps manage stress more effectively, allowing you to bounce back faster from challenges.</li>
        <li><strong>Improved Mental Health:</strong> Contributes to better emotional regulation and reduces the risk of anxiety and depression.</li>
        <li><strong>Stronger Relationships:</strong> Helps navigate relationship challenges and maintain strong bonds with others.</li>
        <li><strong>Increased Self-Worth:</strong> Boosts self-esteem by strengthening coping skills.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Practical Strategies for Building Resilience</h2>
      <ul className="content-list">
        <li><strong>Mindfulness & Meditation:</strong> Staying grounded in the present helps manage emotions.</li>
        <li><strong>Exercise:</strong> Physical activity improves mood and reduces stress.</li>
        <li><strong>Positive Thinking:</strong> Focus on strengths and reframe negative thoughts.</li>
        <li><strong>Social Support:</strong> Strong networks of family and friends provide guidance and comfort.</li>
        <li><strong>Self-Compassion:</strong> Treat yourself with kindness, especially during tough times.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      {loading ? (
        <p className="message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="page-divider" />

      <h3 className="subheading">Join the Emotional Resilience Community</h3>
      <p className="page-text">
        Share your experiences, ask questions, and connect with others focused on building emotional resilience. 
        Whether you're looking for support, resources, or inspiration, this is your space to grow and thrive.
      </p>
      <a href="/forum/Emotional_Resilience" className="btn">Join the Forum</a>
    </div>
  );
};

export default Resilience;
